/**
 * extractFollowupChips — pulls the AI's machine-readable suggestion-chip block
 * out of an assistant message and returns both the cleaned content (without
 * the marker) and the parsed chip definitions.
 *
 * The AI prompt asks the model to append a single line at the very bottom of
 * its response in this format:
 *   <!--FOLLOWUP_CHIPS:[{"type":"clarify","label":"...","question":"..."},
 *                       {"type":"explore","label":"...","question":"..."}]-->
 *
 * If the comment is absent or malformed, this returns the original content
 * with chips:[]. The caller should always render the visible response —
 * chips are an enhancement, never a requirement.
 */

// We tolerate the marker being placed ANYWHERE in the message, not just
// at the very end. The original prompt asks the LLM to put it at the
// bottom, but in practice models occasionally insert it before the
// appendix sections (### Scripture References / ### Commentator Insights
// / etc.) which would leave the marker stranded mid-content. Anchoring
// the regex to end-of-string in that case would fail to strip and the
// raw `<!--FOLLOWUP_CHIPS:[...]-->` text leaks into the rendered chat
// bubble. The `g` flag also makes us strip ALL instances if the model
// somehow emits the comment more than once.
const MARKER_RE = /<!--FOLLOWUP_CHIPS:(.*?)-->/gs;

export function extractFollowupChips(content) {
  if (!content || typeof content !== 'string') {
    return { content: content || '', chips: [] };
  }

  // With the global flag we may match multiple times — keep the FIRST
  // valid chips block we successfully parse. Reset lastIndex defensively
  // because regex literals with `g` carry state.
  MARKER_RE.lastIndex = 0;
  const allMatches = [...content.matchAll(MARKER_RE)];
  if (allMatches.length === 0) {
    return { content, chips: [] };
  }

  let chips = [];
  for (const match of allMatches) {
    try {
      const parsed = JSON.parse(match[1].trim());
      if (Array.isArray(parsed)) {
        chips = parsed
          .filter(
            (c) =>
              c &&
              typeof c === 'object' &&
              typeof c.label === 'string' &&
              typeof c.question === 'string' &&
              c.label.trim().length > 0 &&
              c.question.trim().length > 0,
          )
          .slice(0, 2)
          .map((c) => ({
            type: c.type === 'explore' ? 'explore' : 'clarify',
            label: c.label.trim().slice(0, 80),
            question: c.question.trim(),
          }));
        if (chips.length > 0) break;  // First successful parse wins.
      }
    } catch {
      // Try the next match in case the first one was malformed.
    }
  }

  // Strip every marker (and any surrounding whitespace that would
  // otherwise leave an awkward double-blank line in its place).
  MARKER_RE.lastIndex = 0;
  const cleaned = content
    .replace(MARKER_RE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { content: cleaned, chips };
}
